import React, {useState, useEffect} from 'react'

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
export const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent)

// shared install state. Chrome/Android/desktop fire beforeinstallprompt (→ native
// prompt). iOS Safari has no such event, so we fall back to Share-sheet instructions.
export function useInstall() {
  const [deferred, setDeferred] = useState(null)
  const [installed, setInstalled] = useState(isStandalone())

  useEffect(() => {
    const onPrompt = e => {
      e.preventDefault()
      setDeferred(e)
    }
    const onInstalled = () => {
      setInstalled(true)
      setDeferred(null)
    }
    window.addEventListener('beforeinstallprompt', onPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const promptInstall = async () => {
    if (!deferred) return
    deferred.prompt()
    await deferred.userChoice
    setDeferred(null)
  }

  return {installed, canPrompt: !!deferred, promptInstall}
}

// platform-appropriate "how to add to home screen" line
export function InstallSteps() {
  return (
    <div className="ios-tip">
      {isIOS() ? (
        <>
          In Safari, tap the <b>Share</b> button, then <b>“Add to Home Screen.”</b>
        </>
      ) : (
        <>
          Open your browser menu and choose <b>“Add to Home Screen”</b> (or <b>Install app</b>).
        </>
      )}
    </div>
  )
}

// onboarding version — shown right after signup. Always shows the how-to;
// adds the one-tap native button when the browser supports it.
export function InstallOnboarding() {
  const {installed, canPrompt, promptInstall} = useInstall()
  if (installed) {
    return <div className="ios-tip">yoked is on your home screen — open it there any time.</div>
  }
  return (
    <>
      {canPrompt && (
        <button className="btn btn-primary btn-block" onClick={promptInstall} style={{marginTop: 0}}>
          Add to Home Screen
        </button>
      )}
      <InstallSteps />
    </>
  )
}

// compact version for Settings — only renders when there's something to offer.
export default function Install() {
  const {installed, canPrompt, promptInstall} = useInstall()
  const [showSteps, setShowSteps] = useState(false)
  if (installed) return null
  if (!canPrompt && !isIOS()) return null
  return (
    <>
      <button
        className="btn btn-ghost btn-block"
        onClick={() => (canPrompt ? promptInstall() : setShowSteps(v => !v))}
        style={{marginTop: 14}}>
        Add to Home Screen
      </button>
      {showSteps && <InstallSteps />}
    </>
  )
}
